import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { aggiornaMagazzino, type Impostazioni, type MagazzinoRiepilogo } from "../api";

type Campo = {
  chiave: keyof Impostazioni;
  etichetta: string;
  tipo?: string;
  soloSeModificabile?: boolean;
};

const CAMPI: Campo[] = [
  { chiave: "telefono", etichetta: "Telefono per il corriere", tipo: "tel" },
  { chiave: "referente", etichetta: "Referente" },
  { chiave: "apertura_mattina_da", etichetta: "Mattina dalle", tipo: "time" },
  { chiave: "apertura_mattina_a", etichetta: "Mattina alle", tipo: "time" },
  { chiave: "apertura_pomeriggio_da", etichetta: "Pomeriggio dalle", tipo: "time" },
  { chiave: "apertura_pomeriggio_a", etichetta: "Pomeriggio alle", tipo: "time" },
  { chiave: "ragione_sociale", etichetta: "Ragione sociale", soloSeModificabile: true },
  { chiave: "indirizzo", etichetta: "Indirizzo", soloSeModificabile: true },
  { chiave: "cap", etichetta: "CAP", soloSeModificabile: true },
  { chiave: "localita", etichetta: "Località", soloSeModificabile: true },
  { chiave: "provincia", etichetta: "Provincia (sigla)", soloSeModificabile: true },
];

type Props = { riepilogo: MagazzinoRiepilogo; onChiudi: () => void; onSalvato: () => void };

export function RecapitiDialog({ riepilogo, onChiudi, onSalvato }: Props) {
  const [valori, setValori] = useState<Impostazioni>(riepilogo.impostazioni);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const campi = CAMPI.filter((c) => !c.soloSeModificabile || riepilogo.indirizzo_modificabile);
  const chiudiRef = useRef<HTMLButtonElement>(null);
  // `onChiudi` arriva inline da SchedaMagazzino (una funzione nuova ad ogni
  // suo render): letto da un ref così l'effetto sotto resta SOLO al mount,
  // senza ripescare il valore più vecchio.
  const onChiudiRef = useRef(onChiudi);
  onChiudiRef.current = onChiudi;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // Stesso pattern di CarrierSwitchConfirmDialog: Esc chiude, e il fuoco
  // parte sul tasto non distruttivo (Chiudi), non sul primo campo — un dialog
  // aperto per sbaglio si chiude con un tasto solo, senza aver già scritto
  // in un campo indirizzo.
  //
  // Deps `[]` apposta: con `[onChiudi]` (una funzione nuova ad ogni render
  // del genitore) l'effetto si rifà e ruba di nuovo il fuoco su «Chiudi»
  // mentre l'operatore sta scrivendo in un campo — uno spazio digitato in
  // quel momento «premerebbe» un tasto che ha il fuoco. onChiudi/busy si
  // leggono dai ref qui sopra, sempre aggiornati.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busyRef.current) onChiudiRef.current();
    };
    window.addEventListener("keydown", onKey);
    chiudiRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function salva() {
    setBusy(true);
    setErrore(null);
    try {
      const patch = Object.fromEntries(
        campi.map((c) => [c.chiave, valori[c.chiave]]),
      ) as Partial<Impostazioni>;
      await aggiornaMagazzino(riepilogo.slot, patch);
      onSalvato();
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="recapiti-titolo"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id="recapiti-titolo" className="font-display text-[17px] font-bold text-ink">
            Recapiti di {riepilogo.etichetta}
          </h2>
        </header>
        <div className="grid grid-cols-2 gap-3 px-5 py-4">
          {campi.map((c) => (
            <label
              key={c.chiave}
              className="flex flex-col gap-1 text-[12px] font-semibold text-ink"
            >
              {c.etichetta}
              <input
                type={c.tipo ?? "text"}
                value={String(valori[c.chiave] ?? "")}
                onChange={(e) => setValori((v) => ({ ...v, [c.chiave]: e.target.value }))}
                className="rounded-lg border border-line px-2 py-1.5 text-[13px] font-normal text-ink focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40"
              />
            </label>
          ))}
        </div>
        <footer className="border-t border-line px-5 py-3">
          <ErrorBanner message={errore} className="mb-2" />
          <div className="flex justify-end gap-2">
            <button
              ref={chiudiRef}
              type="button"
              onClick={onChiudi}
              disabled={busy}
              className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-bold text-ink hover:bg-paper"
            >
              Chiudi
            </button>
            <button
              type="button"
              onClick={() => void salva()}
              disabled={busy}
              aria-busy={busy}
              className="rounded-full border border-ink bg-ink px-3.5 py-2 text-[13px] font-bold text-on-ink"
            >
              {busy ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
